import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import {
  ListBox as ReactAriaListBox,
  ListBoxProps as ReactAriaListBoxProps,
  useContextProps,
} from 'react-aria-components';
import { mergeClassNames } from '../../../../utils/mergeClassNames';
import styles from './ListBox.module.scss';
import { SelectBaseProps } from './SelectBase';

export type SelectBaseListBoxProps<T extends object> =
  ReactAriaListBoxProps<T> & {
    /**
     * @default 'medium'
     */
    size?: SelectBaseProps['size'];
  };

export type SelectBaseListBoxContextValue<T extends object> = ContextValue<
  Omit<SelectBaseListBoxProps<T>, 'children'>,
  HTMLUListElement
>;

export const SelectBaseListBoxContext = createContext<
  SelectBaseListBoxContextValue<object>
>({});

/**
 * A styled ListBox from `react-aria-components`. Useful when creating a custom Select.
 *
 * Merges context props from `SelectBaseListBoxContext` with local props (preferring local props).
 */
export const SelectBaseListBox = <T extends object>(
  props: SelectBaseListBoxProps<T>
) => {
  const [contextProps] = useContextProps(props, null, SelectBaseListBoxContext);
  const { className, size = 'medium', ...otherProps } = contextProps;

  return (
    <ReactAriaListBox
      {...otherProps}
      className={(renderProps) =>
        mergeClassNames([styles.listBox, className], renderProps)
      }
      data-size={size}
    />
  );
};
